import {
  OfflineAdjustmentPayloadSchema,
  OfflineOrderPayloadSchema,
} from "../../../domain/contracts/offline-sync";
import { posApi } from "../../api/client";
import {
  getLocalOrder,
  listLocalOrderOperationsForShift,
  listLocalOrdersBySyncStatus,
  listLocalOrdersForBranch,
  listOfflineOrdersForShift,
  markLocalOrderOperationSyncStatus,
  markLocalOrderSyncStatus,
  updateOfflineOrderStatus,
} from "./db";
import { drainLocalCustomerDraftsForBranch } from "./drain-customer-drafts";
import {
  isPricingReplayErrorCode,
  reconcileLocalOrderAfterSync,
} from "./reconcile-after-sync";
import { isCrossShiftPaidOrderPendingSync } from "./local-first-display-helpers";
import {
  buildAdjustmentSyncPayload,
  buildSyncPayloadFromLocalOrder,
} from "./sync-builder";
import type {
  LocalOrderOperationRecord,
  LocalOrderOperationType,
  LocalOrderRecord,
  OfflineOrderRecord,
} from "./types";

export type DrainShiftOfflineOrdersInput = {
  branchId: string;
  deviceId: string;
  shiftId: string;
  pollIntervalMs?: number;
  timeoutMs?: number;
  onProgress?: (progress: DrainShiftOfflineOrdersProgress) => void;
};

type DrainShiftOfflineOrdersResult = {
  synced: number;
  failed: number;
  skipped: number;
  total: number;
};

export type DrainShiftOfflineOrdersProgress = {
  stage:
    | "loading"
    | "no_orders"
    | "marking"
    | "uploading"
    | "waiting"
    | "finalizing"
    | "done"
    | "failed";
  message: string;
  total: number;
  accepted: number;
  rejected: number;
  completed: number;
  failed: number;
  pending: number;
  skipped: number;
  jobId?: string;
};

const DRAINABLE_STATUSES: OfflineOrderRecord["status"][] = ["queued", "failed"];

const DRAINABLE_LOCAL_SYNC_STATUSES: LocalOrderRecord["syncStatus"][] = [
  "queued",
  "failed",
];

const RECONCILE_LOCAL_SYNC_STATUSES: LocalOrderRecord["syncStatus"][] = [
  "syncing",
];

const ADJUSTMENT_OPERATION_TYPES: LocalOrderOperationType[] = [
  "void_order",
  "refund_order",
  "move_table",
  "merge_orders",
];

const DRAINABLE_ADJUSTMENT_SYNC_STATUSES: NonNullable<
  LocalOrderOperationRecord["syncStatus"]
>[] = ["queued", "failed"];

// ຕ້ອງໃຫ້ກົງກັບ `.max(50)` ໃນ SyncOrdersRequestSchema / SyncAdjustmentsRequestSchema
// ຝັ່ງ server. ຖ້າສົ່ງເກີນ server ຈະ reject ທັງ batch (HTTP 400).
const SYNC_BATCH_SIZE = 50;

function chunkArray<T>(items: T[], size: number): T[][] {
  if (items.length <= size) {
    return items.length === 0 ? [] : [items];
  }
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

// ແປງ Zod issues ເປັນຂໍ້ຄວາມສັ້ນໆ ບອກ field ທີ່ຜິດ ເພື່ອໃຫ້ user ຮູ້ວ່າຕ້ອງແກ້ຫຍັງ
function formatPayloadIssues(
  issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey>; message: string }>,
): string {
  const formatted = issues.slice(0, 3).map((issue) => {
    const path = issue.path.map(String).join(".");
    return path ? `${path}: ${issue.message}` : issue.message;
  });
  const more = issues.length > 3 ? ` (+${issues.length - 3})` : "";
  return formatted.join("; ") + more;
}

// ກວດ payload ຂອງ order ກ່ອນສົ່ງ ໃຫ້ກົງກັບ schema ຝັ່ງ server.
// ຄືນ null ຖ້າຜ່ານ, ຄືນຂໍ້ຄວາມຜິດພາດຖ້າບໍ່ຜ່ານ.
function validateOrderPayloadForSync(payload: unknown): string | null {
  const result = OfflineOrderPayloadSchema.safeParse(payload);
  return result.success ? null : formatPayloadIssues(result.error.issues);
}

function validateAdjustmentPayloadForSync(payload: unknown): string | null {
  const result = OfflineAdjustmentPayloadSchema.safeParse(payload);
  return result.success ? null : formatPayloadIssues(result.error.issues);
}

type SyncableOfflineOrder = {
  source: "offline";
  clientOrderId: string;
  payload: OfflineOrderRecord["payload"];
  retryCount: number;
};

type SyncableLocalOrder = {
  source: "local";
  clientOrderId: string;
  payload: ReturnType<typeof buildSyncPayloadFromLocalOrder>;
  retryCount: number;
};

type SyncableOrder = SyncableOfflineOrder | SyncableLocalOrder;
type PreparedAdjustmentOperation = {
  operation: LocalOrderOperationRecord;
  payload: ReturnType<typeof buildAdjustmentSyncPayload>;
};
type SyncInboxOrder = Awaited<
  ReturnType<typeof posApi.listSyncInboxOrders>
>["items"][number];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollJobUntilSettled(
  jobId: string,
  pollIntervalMs: number,
  timeoutMs: number,
  onProgress?: (
    job: Awaited<ReturnType<typeof posApi.getSyncJobStatus>>,
  ) => void,
) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const job = await posApi.getSyncJobStatus(jobId);
    const unsettled = job.orders.some(
      (order) => order.status === "pending" || order.status === "processing",
    );
    onProgress?.(job);
    if (!unsettled) return job;
    await sleep(pollIntervalMs);
  }

  throw new Error("Sync job timed out while waiting for server processing");
}

async function markSyncing(record: SyncableOrder) {
  if (record.source === "local") {
    await markLocalOrderSyncStatus(record.clientOrderId, {
      syncStatus: "syncing",
      lastSyncError: undefined,
    });
    return;
  }

  await updateOfflineOrderStatus(record.clientOrderId, {
    status: "syncing",
    errorMessage: undefined,
  });
}

async function markRejected(
  record: SyncableOrder | undefined,
  clientOrderId: string,
  message: string,
) {
  if (record?.source === "local") {
    await markLocalOrderSyncStatus(clientOrderId, {
      syncStatus: "failed",
      lastSyncError: message,
      retryCount: record.retryCount + 1,
    });
    return;
  }

  await updateOfflineOrderStatus(clientOrderId, {
    status: "failed",
    errorMessage: message,
    retryCount: (record?.retryCount ?? 0) + 1,
  });
}

async function markSynced(
  record: SyncableOrder | undefined,
  clientOrderId: string,
  serverOrderId?: string | null,
  replayResult?: {
    serverCustomerId?: string;
    couponReplay?: Array<{
      code: string;
      applied: boolean;
      errorCode?: string;
      error?: string;
    }>;
  } | null,
) {
  if (record?.source === "local") {
    await markLocalOrderSyncStatus(clientOrderId, {
      status: "SYNCED",
      syncStatus: "synced",
      serverOrderId: serverOrderId ?? undefined,
      lastSyncError: undefined,
    });
    await reconcileLocalOrderAfterSync(clientOrderId, {
      serverOrderId,
      replayResult,
    });
    return;
  }

  await updateOfflineOrderStatus(clientOrderId, {
    status: "synced",
    errorMessage: undefined,
    syncedAt: new Date().toISOString(),
  });
}

async function markServerFailed(
  record: SyncableOrder | undefined,
  clientOrderId: string,
  serverStatus: string,
  message: string,
  errorCode?: string | null,
) {
  if (record?.source === "local") {
    const needsReview =
      serverStatus === "dead" || isPricingReplayErrorCode(errorCode);
    await markLocalOrderSyncStatus(clientOrderId, {
      syncStatus: needsReview ? "needs_review" : "failed",
      lastSyncError: message,
      retryCount: (record.retryCount ?? 0) + 1,
    });
    if (needsReview) {
      await reconcileLocalOrderAfterSync(clientOrderId, {
        errorCode,
        errorMessage: message,
      });
    }
    return;
  }

  await updateOfflineOrderStatus(clientOrderId, {
    status: serverStatus === "dead" ? "needs_review" : "failed",
    errorMessage: message,
    retryCount: (record?.retryCount ?? 0) + 1,
  });
}

const INBOX_LOOKUP_STATUSES = [
  "completed",
  "failed",
  "dead",
  "pending",
  "processing",
] as const;

async function listInboxOrdersByStatus(params: {
  branchId: string;
  deviceId: string;
  shiftId?: string;
}): Promise<SyncInboxOrder[]> {
  const results: SyncInboxOrder[] = [];

  for (const status of INBOX_LOOKUP_STATUSES) {
    let offset = 0;
    const limit = 100;
    while (true) {
      const page = await posApi.listSyncInboxOrders({
        branchId: params.branchId,
        deviceId: params.deviceId,
        shiftId: params.shiftId,
        status,
        limit,
        offset,
      });
      results.push(...page.items);
      offset += page.items.length;
      if (offset >= page.total || page.items.length === 0) break;
    }
  }

  return results;
}

async function listShiftInboxOrders(params: {
  branchId: string;
  deviceId: string;
  shiftId: string;
}): Promise<SyncInboxOrder[]> {
  return listInboxOrdersByStatus(params);
}

function inboxStatusPriority(status: SyncInboxOrder["status"]): number {
  switch (status) {
    case "completed":
    case "resolved":
      return 5;
    case "processing":
      return 3;
    case "pending":
      return 2;
    case "failed":
      return 1;
    case "dead":
      return 0;
    default:
      return 0;
  }
}

function buildInboxByClientOrderId(
  rows: SyncInboxOrder[],
): Map<string, SyncInboxOrder> {
  const map = new Map<string, SyncInboxOrder>();
  for (const row of rows) {
    const existing = map.get(row.clientOrderId);
    if (!existing) {
      map.set(row.clientOrderId, row);
      continue;
    }
    if (inboxStatusPriority(row.status) > inboxStatusPriority(existing.status)) {
      map.set(row.clientOrderId, row);
    }
  }
  return map;
}

async function listLocalOrdersNeedingSyncReconcile(params: {
  branchId: string;
  shiftId: string;
}): Promise<LocalOrderRecord[]> {
  const sameShift = await listLocalOrdersBySyncStatus(
    params.shiftId,
    RECONCILE_LOCAL_SYNC_STATUSES,
  );
  const branchOrders = await listLocalOrdersForBranch(params.branchId);
  const crossShift = branchOrders.filter(
    (order) =>
      order.syncStatus === "syncing" &&
      isCrossShiftPaidOrderPendingSync(order, params.shiftId),
  );

  const byClientOrderId = new Map<string, LocalOrderRecord>();
  for (const order of [...sameShift, ...crossShift]) {
    byClientOrderId.set(order.clientOrderId, order);
  }
  return [...byClientOrderId.values()];
}

async function reconcileLocalSyncingOrders(params: {
  branchId: string;
  deviceId: string;
  shiftId: string;
}): Promise<{ completed: number; failed: number; pending: number }> {
  const syncingOrders = await listLocalOrdersNeedingSyncReconcile({
    branchId: params.branchId,
    shiftId: params.shiftId,
  });
  if (syncingOrders.length === 0) {
    return { completed: 0, failed: 0, pending: 0 };
  }

  const inboxRows = await listInboxOrdersByStatus({
    branchId: params.branchId,
    deviceId: params.deviceId,
  });
  const inboxByClientOrderId = buildInboxByClientOrderId(inboxRows);

  let completed = 0;
  let failed = 0;
  let pending = 0;

  for (const order of syncingOrders) {
    const inbox = inboxByClientOrderId.get(order.clientOrderId);
    if (!inbox) {
      await markLocalOrderSyncStatus(order.clientOrderId, {
        syncStatus: "failed",
        lastSyncError:
          "Sync status was unknown on server. Marked failed so it can be retried explicitly.",
      });
      failed += 1;
      continue;
    }

    if (inbox.status === "completed" || inbox.status === "resolved") {
      await markLocalOrderSyncStatus(order.clientOrderId, {
        status: "SYNCED",
        syncStatus: "synced",
        serverOrderId: inbox.serverOrderId ?? undefined,
        lastSyncError: undefined,
      });
      await reconcileLocalOrderAfterSync(order.clientOrderId, {
        serverOrderId: inbox.serverOrderId,
        replayResult: inbox.replayResult ?? null,
      });
      completed += 1;
      continue;
    }

    if (inbox.status === "failed" || inbox.status === "dead") {
      await markLocalOrderSyncStatus(order.clientOrderId, {
        syncStatus:
          inbox.status === "dead" || isPricingReplayErrorCode(inbox.errorCode)
            ? "needs_review"
            : "failed",
        lastSyncError:
          inbox.errorMessage ??
          `Server sync did not complete successfully (${inbox.status})`,
        retryCount: order.retryCount + 1,
      });
      await reconcileLocalOrderAfterSync(order.clientOrderId, {
        errorCode: inbox.errorCode,
        errorMessage: inbox.errorMessage,
      });
      failed += 1;
      continue;
    }

    pending += 1;
  }

  return { completed, failed, pending };
}

async function prepareAdjustmentOperationsForShift(shiftId: string): Promise<{
  ready: PreparedAdjustmentOperation[];
  waitingForOrderSync: LocalOrderOperationRecord[];
  unsupported: LocalOrderOperationRecord[];
  invalid: Array<{ operation: LocalOrderOperationRecord; error: string }>;
}> {
  const operations = await listLocalOrderOperationsForShift(shiftId, {
    types: ADJUSTMENT_OPERATION_TYPES,
    syncStatuses: DRAINABLE_ADJUSTMENT_SYNC_STATUSES,
  });
  const ready: PreparedAdjustmentOperation[] = [];
  const waitingForOrderSync: LocalOrderOperationRecord[] = [];
  const unsupported: LocalOrderOperationRecord[] = [];
  const invalid: Array<{
    operation: LocalOrderOperationRecord;
    error: string;
  }> = [];

  for (const operation of operations) {
    const order = await getLocalOrder(operation.clientOrderId);
    if (
      operation.type === "refund_order" &&
      !order?.serverOrderId &&
      order?.syncStatus !== "synced"
    ) {
      waitingForOrderSync.push(operation);
      continue;
    }

    let payload: PreparedAdjustmentOperation["payload"];
    try {
      payload = buildAdjustmentSyncPayload(operation, order);
    } catch {
      unsupported.push(operation);
      continue;
    }

    const validationError = validateAdjustmentPayloadForSync(payload);
    if (validationError) {
      invalid.push({ operation, error: validationError });
      continue;
    }

    ready.push({ operation, payload });
  }

  return { ready, waitingForOrderSync, unsupported, invalid };
}

async function markAdjustmentSyncing(record: PreparedAdjustmentOperation) {
  await markLocalOrderOperationSyncStatus(record.operation.operationId, {
    syncStatus: "syncing",
    lastSyncError: undefined,
  });
}

async function markAdjustmentRejected(
  record: PreparedAdjustmentOperation | undefined,
  operationId: string,
  message: string,
) {
  await markLocalOrderOperationSyncStatus(operationId, {
    syncStatus: "failed",
    lastSyncError: message,
    retryCount: (record?.operation.retryCount ?? 0) + 1,
  });
}

async function markAdjustmentSynced(operationId: string) {
  await markLocalOrderOperationSyncStatus(operationId, {
    syncStatus: "synced",
    lastSyncError: undefined,
    syncedAt: new Date().toISOString(),
  });
}

async function markAdjustmentServerFailed(
  record: PreparedAdjustmentOperation | undefined,
  operationId: string,
  serverStatus: string,
  message: string,
) {
  await markLocalOrderOperationSyncStatus(operationId, {
    syncStatus: serverStatus === "dead" ? "needs_review" : "failed",
    lastSyncError: message,
    retryCount: (record?.operation.retryCount ?? 0) + 1,
  });
}

async function syncPreparedAdjustments(params: {
  branchId: string;
  deviceId: string;
  shiftId: string;
  prepared: PreparedAdjustmentOperation[];
  pollIntervalMs: number;
  timeoutMs: number;
  onProgress?: (progress: DrainShiftOfflineOrdersProgress) => void;
  baseTotal: number;
  baseCompleted: number;
  baseFailed: number;
}): Promise<{
  synced: number;
  failed: number;
  pending: number;
  skipped: number;
}> {
  if (params.prepared.length === 0) {
    return { synced: 0, failed: 0, pending: 0, skipped: 0 };
  }

  const recordsByOperationId = new Map(
    params.prepared.map((record) => [record.operation.operationId, record]),
  );

  let synced = 0;
  let failed = 0;
  let skipped = 0;

  const chunks = chunkArray(params.prepared, SYNC_BATCH_SIZE);

  for (const chunk of chunks) {
    for (const record of chunk) {
      await markAdjustmentSyncing(record);
    }

    params.onProgress?.({
      stage: "uploading",
      message: `ກຳລັງສົ່ງ ${chunk.length} adjustment operation ໄປ server...`,
      total: params.baseTotal,
      accepted: synced,
      rejected: failed,
      completed: params.baseCompleted + synced,
      failed: params.baseFailed + failed,
      pending: chunk.length,
      skipped,
    });

    const response = await posApi.syncOfflineAdjustments({
      branchId: params.branchId,
      deviceId: params.deviceId,
      shiftId: params.shiftId,
      operations: chunk.map((record) => record.payload),
    });

    for (const rejected of response.rejected) {
      failed += 1;
      await markAdjustmentRejected(
        recordsByOperationId.get(rejected.operationId),
        rejected.operationId,
        `${rejected.errorCode}: ${rejected.message}`,
      );
    }

    const acceptedIds = new Set(
      response.accepted.map((accepted) => accepted.operationId),
    );
    skipped += chunk.length - acceptedIds.size - response.rejected.length;

    if (response.accepted.length === 0) {
      continue;
    }

    params.onProgress?.({
      stage: "waiting",
      message: `server ຮັບແລ້ວ ${response.accepted.length} adjustment operation ກຳລັງລໍຖ້າ worker process...`,
      total: params.baseTotal,
      accepted: response.accepted.length,
      rejected: response.rejected.length,
      completed: params.baseCompleted + synced,
      failed: params.baseFailed + failed,
      pending: response.accepted.length,
      skipped,
      jobId: response.jobId,
    });

    const job = await pollJobUntilSettled(
      response.jobId,
      params.pollIntervalMs,
      params.timeoutMs,
    );
    const operationsById = new Map(
      job.orders.map((operation) => [operation.clientOrderId, operation]),
    );

    for (const accepted of response.accepted) {
      const operationStatus = operationsById.get(accepted.operationId);
      const serverStatus = operationStatus?.status ?? accepted.status;
      const sourceRecord = recordsByOperationId.get(accepted.operationId);
      if (
        serverStatus === "completed" ||
        accepted.status === "already_completed"
      ) {
        synced += 1;
        await markAdjustmentSynced(accepted.operationId);
        continue;
      }

      failed += 1;
      await markAdjustmentServerFailed(
        sourceRecord,
        accepted.operationId,
        serverStatus,
        operationStatus?.errorMessage ??
          `Server adjustment sync did not complete successfully (${serverStatus})`,
      );
    }
  }

  return { synced, failed, pending: 0, skipped };
}

export async function drainShiftOfflineOrders({
  branchId,
  deviceId,
  shiftId,
  pollIntervalMs = 1_000,
  timeoutMs = 120_000,
  onProgress,
}: DrainShiftOfflineOrdersInput): Promise<DrainShiftOfflineOrdersResult> {
  onProgress?.({
    stage: "loading",
    message: "ກຳລັງກວດສອບ order offline...",
    total: 0,
    accepted: 0,
    rejected: 0,
    completed: 0,
    failed: 0,
    pending: 0,
    skipped: 0,
  });

  const customerDraftResult = await drainLocalCustomerDraftsForBranch({
    branchId,
    shiftId,
    onProgress: (progress) => {
      onProgress?.({
        stage: "uploading",
        message: progress.message,
        total: progress.total,
        accepted: progress.synced,
        rejected: progress.failed,
        completed: progress.synced,
        failed: progress.failed,
        pending: progress.pending,
        skipped: 0,
      });
    },
  });

  const reconciled = await reconcileLocalSyncingOrders({
    branchId,
    deviceId,
    shiftId,
  });
  const adjustmentPreparation =
    await prepareAdjustmentOperationsForShift(shiftId);
  const pendingAdjustmentCount =
    adjustmentPreparation.ready.length +
    adjustmentPreparation.waitingForOrderSync.length;

  for (const operation of adjustmentPreparation.unsupported) {
    await markLocalOrderOperationSyncStatus(operation.operationId, {
      syncStatus: "failed",
      lastSyncError: `Unsupported adjustment operation type: ${operation.type}`,
      retryCount: (operation.retryCount ?? 0) + 1,
    });
  }

  for (const { operation, error } of adjustmentPreparation.invalid) {
    await markLocalOrderOperationSyncStatus(operation.operationId, {
      syncStatus: "failed",
      lastSyncError: `VALIDATION_ERROR: ${error}`,
      retryCount: (operation.retryCount ?? 0) + 1,
    });
  }

  const [localRecordsSameShift, localRecordsCrossShiftPaid, offlineRecords] =
    await Promise.all([
      listLocalOrdersBySyncStatus(shiftId, DRAINABLE_LOCAL_SYNC_STATUSES),
      listLocalOrdersForBranch(branchId).then((orders) =>
        orders.filter(
          (order) =>
            (order.completedShiftId ?? order.shiftId) === shiftId &&
            order.shiftId !== shiftId &&
            order.status !== "OPEN" &&
            DRAINABLE_LOCAL_SYNC_STATUSES.includes(order.syncStatus),
        ),
      ),
      listOfflineOrdersForShift(shiftId, DRAINABLE_STATUSES),
    ]);
  const localRecordsById = new Map<string, LocalOrderRecord>();
  for (const record of [...localRecordsSameShift, ...localRecordsCrossShiftPaid]) {
    localRecordsById.set(record.clientOrderId, record);
  }
  const localRecords = [...localRecordsById.values()];
  const records: SyncableOrder[] = [
    ...localRecords.map(
      (record): SyncableOrder => ({
        source: "local",
        clientOrderId: record.clientOrderId,
        payload: buildSyncPayloadFromLocalOrder(record),
        retryCount: record.retryCount,
      }),
    ),
    ...offlineRecords.map(
      (record): SyncableOrder => ({
        source: "offline",
        clientOrderId: record.clientOrderId,
        payload: record.payload,
        retryCount: record.retryCount,
      }),
    ),
  ];
  const recordsByClientId = new Map(
    records.map((record) => [record.clientOrderId, record]),
  );
  const total =
    customerDraftResult.total +
    records.length +
    reconciled.completed +
    reconciled.pending +
    pendingAdjustmentCount;
  if (records.length === 0 && adjustmentPreparation.ready.length > 0) {
    const adjustmentResult = await syncPreparedAdjustments({
      branchId,
      deviceId,
      shiftId,
      prepared: adjustmentPreparation.ready,
      pollIntervalMs,
      timeoutMs,
      onProgress,
      baseTotal: total,
      baseCompleted: customerDraftResult.synced + reconciled.completed,
      baseFailed:
        customerDraftResult.failed + reconciled.failed + reconciled.pending,
    });

    const waiting = adjustmentPreparation.waitingForOrderSync.length;
    onProgress?.({
      stage:
        customerDraftResult.failed > 0 ||
        adjustmentResult.failed > 0 ||
        waiting > 0
          ? "failed"
          : "done",
      message:
        customerDraftResult.failed > 0 ||
        adjustmentResult.failed > 0 ||
        waiting > 0
          ? `sync adjustment ສຳເລັດ ${adjustmentResult.synced}, ລົ້ມເຫຼວ/ລໍຖ້າ ${adjustmentResult.failed + waiting}`
          : `sync adjustment operation ສຳເລັດ ${adjustmentResult.synced}`,
      total,
      accepted: adjustmentResult.synced,
      rejected: adjustmentResult.failed,
      completed:
        customerDraftResult.synced +
        reconciled.completed +
        adjustmentResult.synced,
      failed:
        customerDraftResult.failed +
        reconciled.failed +
        reconciled.pending +
        adjustmentResult.failed +
        waiting,
      pending: waiting,
      skipped: adjustmentResult.skipped,
    });

    return {
      synced:
        customerDraftResult.synced +
        reconciled.completed +
        adjustmentResult.synced,
      failed:
        customerDraftResult.failed +
        reconciled.failed +
        reconciled.pending +
        adjustmentResult.failed +
        waiting,
      skipped: adjustmentResult.skipped,
      total,
    };
  }

  if (records.length === 0) {
    const noBlockingWork =
      customerDraftResult.total > 0 &&
      customerDraftResult.failed === 0 &&
      reconciled.pending === 0 &&
      pendingAdjustmentCount === 0;
    const waitingForWork =
      customerDraftResult.failed > 0 ||
      reconciled.pending > 0 ||
      pendingAdjustmentCount > 0;
    onProgress?.({
      stage: noBlockingWork ? "done" : waitingForWork ? "waiting" : "no_orders",
      message: noBlockingWork
        ? `sync customer draft ສຳເລັດ ${customerDraftResult.synced} ລາຍການ`
        : customerDraftResult.failed > 0
          ? `customer draft sync ລົ້ມເຫຼວ ${customerDraftResult.failed} ລາຍການ`
          : reconciled.pending > 0
            ? `ມີ ${reconciled.pending} order ທີ່ server ກຳລັງ process ຢູ່ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ`
            : pendingAdjustmentCount > 0
              ? `ມີ ${pendingAdjustmentCount} adjustment operation ລໍຖ້າ order ຫຼັກ sync ກ່ອນ`
              : "ບໍ່ມີ order offline ທີ່ຕ້ອງ sync",
      total,
      accepted: 0,
      rejected: 0,
      completed: customerDraftResult.synced + reconciled.completed,
      failed: customerDraftResult.failed + reconciled.failed,
      pending: reconciled.pending,
      skipped: pendingAdjustmentCount,
    });
    return {
      synced: customerDraftResult.synced + reconciled.completed,
      failed:
        customerDraftResult.failed +
        reconciled.failed +
        reconciled.pending +
        pendingAdjustmentCount,
      skipped: pendingAdjustmentCount,
      total,
    };
  }

  onProgress?.({
    stage: "marking",
    message: `ພົບ ${records.length} order offline ກຳລັງກຽມ sync...${
      adjustmentPreparation.ready.length > 0
        ? ` (${adjustmentPreparation.ready.length} adjustment ຈະ sync ຕໍ່ຈາກ order)`
        : ""
    }`,
    total,
    accepted: 0,
    rejected: 0,
    completed: customerDraftResult.synced + reconciled.completed,
    failed: customerDraftResult.failed + reconciled.pending,
    pending: records.length + reconciled.pending,
    skipped: 0,
  });

  let synced = customerDraftResult.synced + reconciled.completed;
  let failed = customerDraftResult.failed + reconciled.pending;
  let skipped = 0;
  let acceptedTotal = 0;
  let rejectedTotal = 0;
  let lastJobId: string | undefined;

  // Validate ແຕ່ລະ order ກ່ອນສົ່ງ ເພື່ອບໍ່ໃຫ້ 1 ລາຍການທີ່ຜິດ schema
  // ເຮັດໃຫ້ batch ທັງໝົດ reject (HTTP 400). ຕົວທີ່ຜິດຈະຖືກ mark failed ພ້ອມ field ທີ່ຜິດ.
  const validRecords: SyncableOrder[] = [];
  for (const record of records) {
    const validationError = validateOrderPayloadForSync(record.payload);
    if (validationError) {
      failed += 1;
      await markRejected(
        record,
        record.clientOrderId,
        `VALIDATION_ERROR: ${validationError}`,
      );
      continue;
    }
    validRecords.push(record);
  }

  const invalidOrderCount = records.length - validRecords.length;
  if (invalidOrderCount > 0) {
    onProgress?.({
      stage: "marking",
      message: `ພົບ ${invalidOrderCount} order ທີ່ຂໍ້ມູນບໍ່ຖືກຕ້ອງ (validation) — ຂ້າມ ແລະ mark failed`,
      total,
      accepted: acceptedTotal,
      rejected: rejectedTotal,
      completed: synced,
      failed,
      pending: validRecords.length + reconciled.pending,
      skipped,
      jobId: lastJobId,
    });
  }

  const orderChunks = chunkArray(validRecords, SYNC_BATCH_SIZE);

  for (const [chunkIndex, chunk] of orderChunks.entries()) {
    for (const record of chunk) {
      await markSyncing(record);
    }

    onProgress?.({
      stage: "uploading",
      message:
        orderChunks.length > 1
          ? `ກຳລັງສົ່ງ batch ${chunkIndex + 1}/${orderChunks.length} (${chunk.length} order) ໄປ server...`
          : `ກຳລັງສົ່ງ ${chunk.length} order ໄປ server...`,
      total,
      accepted: acceptedTotal,
      rejected: rejectedTotal,
      completed: synced,
      failed,
      pending: chunk.length + reconciled.pending,
      skipped,
      jobId: lastJobId,
    });

    const response = await posApi.syncOfflineOrders({
      branchId,
      deviceId,
      shiftId,
      orders: chunk.map((record) => record.payload),
    });

    for (const rejected of response.rejected) {
      failed += 1;
      rejectedTotal += 1;
      await markRejected(
        recordsByClientId.get(rejected.clientOrderId),
        rejected.clientOrderId,
        `${rejected.errorCode}: ${rejected.message}`,
      );
    }

    const acceptedIds = new Set(
      response.accepted.map((accepted) => accepted.clientOrderId),
    );
    skipped += chunk.length - acceptedIds.size - response.rejected.length;
    acceptedTotal += response.accepted.length;

    if (response.accepted.length === 0) {
      continue;
    }
    lastJobId = response.jobId;

    onProgress?.({
      stage: "waiting",
      message: `server ຮັບແລ້ວ ${response.accepted.length} order ກຳລັງລໍຖ້າ worker process...`,
      total,
      accepted: acceptedTotal,
      rejected: rejectedTotal,
      completed: synced,
      failed,
      pending: response.accepted.length + reconciled.pending,
      skipped,
      jobId: response.jobId,
    });

    const baseSynced = synced;
    const baseFailed = failed;
    const job = await pollJobUntilSettled(
      response.jobId,
      pollIntervalMs,
      timeoutMs,
      (jobStatus) => {
        const completed = jobStatus.orders.filter(
          (order) => order.status === "completed",
        ).length;
        const serverFailed = jobStatus.orders.filter(
          (order) => order.status === "failed" || order.status === "dead",
        ).length;
        const pending = jobStatus.orders.filter(
          (order) =>
            order.status === "pending" || order.status === "processing",
        ).length;

        onProgress?.({
          stage: "waiting",
          message: `worker ກຳລັງ process: ສຳເລັດ ${completed}, ລໍຖ້າ ${pending}, ລົ້ມເຫຼວ ${serverFailed}`,
          total,
          accepted: acceptedTotal,
          rejected: rejectedTotal,
          completed: baseSynced + completed,
          failed: baseFailed + serverFailed,
          pending: pending + reconciled.pending,
          skipped,
          jobId: response.jobId,
        });
      },
    );
    const ordersByClientId = new Map(
      job.orders.map((order) => [order.clientOrderId, order]),
    );

    for (const accepted of response.accepted) {
      const serverOrder = ordersByClientId.get(accepted.clientOrderId);
      const serverStatus = serverOrder?.status ?? accepted.status;
      const sourceRecord = recordsByClientId.get(accepted.clientOrderId);
      if (
        serverStatus === "completed" ||
        accepted.status === "already_completed"
      ) {
        synced += 1;
        await markSynced(
          sourceRecord,
          accepted.clientOrderId,
          serverOrder?.serverOrderId ?? accepted.serverOrderId,
          serverOrder?.replayResult ?? null,
        );
        continue;
      }

      failed += 1;
      await markServerFailed(
        sourceRecord,
        accepted.clientOrderId,
        serverStatus,
        serverOrder?.errorMessage ??
          `Server sync did not complete successfully (${serverStatus})`,
        serverOrder?.errorCode,
      );
    }
  }

  const postOrderAdjustmentPreparation =
    await prepareAdjustmentOperationsForShift(shiftId);
  const adjustmentResult = await syncPreparedAdjustments({
    branchId,
    deviceId,
    shiftId,
    prepared: postOrderAdjustmentPreparation.ready,
    pollIntervalMs,
    timeoutMs,
    onProgress,
    baseTotal: total,
    baseCompleted: synced,
    baseFailed: failed,
  });
  const waitingAdjustments =
    postOrderAdjustmentPreparation.waitingForOrderSync.length;
  const totalFailed = failed + adjustmentResult.failed + waitingAdjustments;
  const totalSkipped = skipped + adjustmentResult.skipped;

  onProgress?.({
    stage: totalFailed > 0 ? "failed" : "done",
    message:
      totalFailed > 0
        ? `sync ສຳເລັດ ${synced + adjustmentResult.synced} ລາຍການ, ລົ້ມເຫຼວ/ລໍຖ້າ ${totalFailed} ລາຍການ`
        : `sync order/adjustment offline ສຳເລັດ ${synced + adjustmentResult.synced} ລາຍການ`,
    total,
    accepted: acceptedTotal,
    rejected: rejectedTotal,
    completed: synced + adjustmentResult.synced,
    failed: totalFailed,
    pending: reconciled.pending,
    skipped: totalSkipped,
    jobId: lastJobId,
  });

  return {
    synced: synced + adjustmentResult.synced,
    failed: totalFailed,
    skipped: totalSkipped,
    total,
  };
}
